import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { CSSProperties } from 'react';
import type { ContentPack } from '../packs/types';
import type { Adapters } from '../adapters/types';
import { applyBorder } from '../pixel/border';
import { renderBuild } from './render';
import { useContainerSize } from './hooks/useContainerSize';
import { useAnimationFrame } from './hooks/useAnimationFrame';
import { applyBob, applyShimmer } from './animation';
import { drawEffect } from './effects';
import type { EffectType } from './effects';
import { NameEditor } from './ui/NameEditor';
import { Swatch } from './ui/Swatch';
import { ColorPicker } from './ui/ColorPicker';
import { decodeShare } from './share';
import { useComposerState } from './hooks/useComposerState';
import type { ColorState } from './colors';
import { useExport } from './hooks/useExport';
import { CanvasStage } from './ui/CanvasStage';
import { ExportPanel } from './ui/ExportPanel';
import { StatsPanel } from './ui/StatsPanel';

interface ComposerProps {
  pack: ContentPack;
  adapters: Adapters;
  packSwitcher?: React.ReactNode;
}

const BORDER_PRESETS = ['#000000', '#1a1a1a', '#ffffff', '#8a4a1a', '#1a3050'];

// One stable setter per ColorState scalar — keeps ColorPicker's `cfg` array
// shape identical to before without reintroducing 14 useStates.
function makeColorSetters(setColors: React.Dispatch<React.SetStateAction<ColorState>>) {
  const set = <K extends keyof ColorState>(k: K) =>
    (v: ColorState[K]) => setColors(c => ({ ...c, [k]: v }));
  return {
    setHue: set('hue'),
    setAccentHue: set('accentHue'),
    setGlowHue: set('glowHue'),
    setDetailHue: set('detailHue'),
    setTrimHue: set('trimHue'),
    setBackgroundHue: set('backgroundHue'),
    setPrimarySat: set('primarySat'),
    setAccentSat: set('accentSat'),
    setGlowSat: set('glowSat'),
    setDetailSat: set('detailSat'),
    setTrimSat: set('trimSat'),
    setBgSat: set('bgSat'),
    setBorderEnabled: set('borderEnabled'),
    setBorderColor: set('borderColor'),
  };
}

export function Composer({ pack, adapters, packSwitcher }: ComposerProps) {
  const { state, setBuild, setColors, actions, fleet } = useComposerState(pack, adapters);
  const { build, colors, background, palette, baseCategoryId } = state;
  const setters = useState(() => makeColorSetters(setColors))[0];

  const { selectPart, randomize, loadFleet, saveFleet, removeFleet } = actions;

  const [activePicker, setActivePicker] = useState<string | null>(null);
  // Click-suppression window after the picker dismisses. Defends against the iOS
  // synthesized-click that fires on the underlying swatch right after the canvas
  // tap closes the picker (would otherwise instantly reopen a picker).
  const swatchSuppressUntilRef = useRef(0);
  const closePicker = useCallback(() => {
    swatchSuppressUntilRef.current = Date.now() + 350;
    setActivePicker(null);
  }, []);
  const handleSwatchClick = useCallback((key: string) => {
    if (Date.now() < swatchSuppressUntilRef.current) return;
    setActivePicker(prev => prev === key ? null : key);
  }, []);
  const [activeCategory, setActiveCategory] = useState(pack.categories[0]?.id ?? '');
  const [nameEditorOpen, setNameEditorOpen] = useState(false);
  const [effectType, setEffectType] = useState<EffectType>('none');
  const [effectSpeed, setEffectSpeed] = useState(0.5);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const offRef = useRef<HTMLCanvasElement | null>(null);
  const tRef = useRef(0);
  const { ref: stageRef, size: stageSize } = useContainerSize(adapters.lifecycle);
  const side = Math.floor(Math.min(stageSize.width || pack.rasterSize * 4, stageSize.height || pack.rasterSize * 4));

  const [insets, setInsets] = useState(() => adapters.lifecycle.safeArea());
  useEffect(() => {
    // Re-read on resize/orientation change — safe-area changes on iOS rotation
    // and during display-fold transitions, not just at mount.
    const reread = () => {
      const next = adapters.lifecycle.safeArea();
      setInsets(prev =>
        prev.top === next.top && prev.bottom === next.bottom && prev.left === next.left && prev.right === next.right
          ? prev : next
      );
    };
    reread();
    window.addEventListener('resize', reread);
    window.addEventListener('orientationchange', reread);
    return () => {
      window.removeEventListener('resize', reread);
      window.removeEventListener('orientationchange', reread);
    };
  }, [adapters.lifecycle]);

  const statValues = pack.computeStats(build);

  useEffect(() => { loadFleet(); }, [loadFleet]);

  useEffect(() => {
    const c = offRef.current ?? document.createElement('canvas');
    c.width = pack.rasterSize;
    c.height = pack.rasterSize;
    offRef.current = c;
  }, [pack.rasterSize]);

  useAnimationFrame((t) => {
    tRef.current = t;
    const off = offRef.current;
    const main = canvasRef.current;
    if (!off || !main) return;
    const reducedMotion = adapters.lifecycle.prefersReducedMotion();
    const animT = reducedMotion ? 0 : t;
    const octx = off.getContext('2d', { willReadFrequently: true });
    const ctx = main.getContext('2d');
    if (!octx || !ctx) return;
    octx.imageSmoothingEnabled = false;
    renderBuild(octx, build, pack, palette, animT);
    if (colors.borderEnabled) applyBorder(octx, colors.borderColor);
    if (!reducedMotion) {
      // Shimmer only matters when the accent has saturation — at sat=0 the
      // accent collapses to gray and the brighten/darken modulation is
      // imperceptible, so skip the per-frame ImageData scan entirely.
      if (colors.accentSat > 0) applyShimmer(octx, palette.accentLight, t, pack.rasterSize);
      applyBob(octx, t, pack.rasterSize);
    }
    ctx.imageSmoothingEnabled = false;
    if (!pack.drawBackground) {
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, main.width, main.height);
    }
    drawEffect(ctx, effectType, effectSpeed * effectSpeed * 4, t, main.width, main.height, palette);
    ctx.drawImage(off, 0, 0, main.width, main.height);
  }, adapters.lifecycle);

  const { exportPng, exportAns, exportSvg, getPngDataUrl } = useExport(offRef, pack, build, palette, background, colors.borderEnabled, colors.borderColor);

  const handleNameSave = (name: string, tagline: string) => {
    setBuild(s => ({ ...s, name: name || undefined, tagline: tagline || undefined }));
  };

  const randomizeOnMount = useCallback(() => {
    const hash = adapters.navigation.readHash();
    const match = hash.match(/[#&]s=([^&]*)/);
    let shouldSkipRandomization = false;

    if (match) {
      const decoded = decodeShare(match[1]);
      if (decoded && decoded.packId === pack.id) {
        shouldSkipRandomization = true;
      }
    }

    if (!shouldSkipRandomization) {
      randomize();
    }
  }, [randomize, adapters.navigation, pack.id]);

  useEffect(() => {
    const timer = setTimeout(randomizeOnMount, 0);
    return () => clearTimeout(timer);
  }, [randomizeOnMount]);

  const currentSelection = (categoryId: string): string | null | undefined =>
    categoryId === baseCategoryId ? build[baseCategoryId] as string | undefined : build[categoryId] as string | null | undefined;

  const slotFilled = (categoryId: string) => {
    const sel = currentSelection(categoryId);
    return sel !== null && sel !== undefined;
  };

  // Sync body background + theme-color so safe-area gutters match the canvas background
  useEffect(() => {
    adapters.lifecycle.setThemeColor(background);
  }, [background, adapters.lifecycle]);

  const activeCategoryDef = pack.categories.find(c => c.id === activeCategory);
  const categoryIsOptional = activeCategory !== baseCategoryId;

  return (
    <div
      className="composer-root flex flex-col overflow-hidden mx-auto"
      style={{
        '--bg': background,
        height: '100dvh',
        maxWidth: 560,
        paddingTop: Math.max(insets.top, 0),
        paddingBottom: Math.max(insets.bottom, 0),
      } as CSSProperties}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2 shrink-0" style={{ background: 'var(--bg)' }}>
        <h1 className="text-lg font-bold text-white tracking-widest shrink-0">{pack.metadata?.title ?? 'SPRITE'}</h1>
        <button
          className="flex items-center gap-1 text-sm text-slate-400 hover:text-white min-w-0 flex-1"
          onClick={() => setNameEditorOpen(true)}
        >
          <span className="truncate">{build.name ?? 'Untitled'}</span>
          <span className="shrink-0 text-slate-600 hover:text-slate-400" style={{ fontSize: 11 }}>✏</span>
        </button>
        <button
          className="shrink-0 text-slate-500 hover:text-white font-bold"
          style={{ fontSize: 18, lineHeight: 1 }}
          title="Randomize"
          onClick={randomize}
        >↺</button>
        {packSwitcher && <div className="shrink-0">{packSwitcher}</div>}
      </div>

      {/* Canvas stage */}
      <CanvasStage stageRef={stageRef} canvasRef={canvasRef} pack={pack} side={side} />

      {/* Controls — scrollable */}
      <div className="flex-1 overflow-y-auto min-h-0 relative" style={{ background: '#0f172a' }}>
        {activePicker !== null && (
          <div className="absolute inset-0 z-40 pointer-events-none" style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }} />
        )}

        {/* Category pills — wrapping grid so all are visible on desktop */}
        <div className="flex flex-wrap gap-1.5 px-3 pt-3 pb-2">
          {pack.categories.map(cat => {
            const active = activeCategory === cat.id;
            const isOptional = cat.id !== baseCategoryId;
            const filled = slotFilled(cat.id);
            return (
              <button
                key={cat.id}
                className="text-xs font-semibold whitespace-nowrap flex items-center gap-1 transition-colors"
                style={{
                  padding: '4px 10px',
                  borderRadius: 20,
                  background: active ? 'rgba(245,158,11,0.18)' : '#1a2640',
                  border: active ? '1px solid #f59e0b' : '1px solid #2d3f58',
                  color: active ? '#fcd34d' : (isOptional && !filled) ? '#3d5070' : '#64748b',
                }}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.label}
                {isOptional && (
                  <span style={{ fontSize: 6, color: filled ? '#4ade80' : '#273449' }}>●</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Part grid — Fix #4: label optional empty state */}
        <div className="grid grid-cols-4 gap-2 p-3">
          {categoryIsOptional && (
            <button
              className="py-2.5 px-1 rounded-lg text-xs text-center font-medium border transition-colors"
              style={currentSelection(activeCategory) == null
                ? { background: '#1e3a5f', borderColor: '#3b82f6', color: '#93c5fd' }
                : { background: '#1e3050', borderColor: '#2d4870', color: '#3d5070' }}
              onClick={() => selectPart(activeCategory, null)}
            >
              — None
            </button>
          )}
          {activeCategoryDef?.keys
            .filter(key => {
              const part = pack.parts[key];
              if (!part?.compatibleSpecies) return true;
              const speciesId = build[baseCategoryId] as string | undefined;
              return speciesId ? part.compatibleSpecies.includes(speciesId) : true;
            })
            .map(key => {
            const item = pack.hulls[key] ?? pack.parts[key];
            const selected = currentSelection(activeCategory) === key;
            return (
              <button
                key={key}
                className="py-2.5 px-1 rounded-lg text-xs text-center truncate font-medium border transition-colors"
                style={selected
                  ? { background: '#78350f', borderColor: '#f59e0b', color: '#fcd34d' }
                  : { background: '#1e3050', borderColor: '#2d4870', color: '#94a3b8' }}
                onClick={() => selectPart(activeCategory, key)}
              >
                {item?.name ?? key}
              </button>
            );
          })}
        </div>

        <div className="border-t border-slate-800 mx-2 mb-2" />

        {/* Compact color swatch row */}
        {(() => {
          const labels = pack.colorLabels ?? {};
          const cfg = [
            { key: 'primary', label: labels.primary ?? 'Color',  hue: colors.hue,           onChange: setters.setHue,           sat: colors.primarySat, onSatChange: setters.setPrimarySat },
            { key: 'accent',  label: labels.accent  ?? 'Accent', hue: colors.accentHue,     onChange: setters.setAccentHue,     sat: colors.accentSat,  onSatChange: setters.setAccentSat },
            { key: 'glow',    label: labels.glow    ?? 'Window', hue: colors.glowHue,       onChange: setters.setGlowHue,       sat: colors.glowSat,    onSatChange: setters.setGlowSat },
            { key: 'detail',  label: labels.detail  ?? 'Detail', hue: colors.detailHue,     onChange: setters.setDetailHue,     sat: colors.detailSat,  onSatChange: setters.setDetailSat },
            { key: 'trim',    label: labels.trim    ?? 'Trim',   hue: colors.trimHue,       onChange: setters.setTrimHue,       sat: colors.trimSat,    onSatChange: setters.setTrimSat },
            { key: 'bg',      label: 'BG',                       hue: colors.backgroundHue, onChange: setters.setBackgroundHue, sat: colors.bgSat,       onSatChange: setters.setBgSat },
          ];
          return (
            <>
              <div className="px-3 pb-3 flex gap-2">
                {cfg.map(({ key, label: lbl, hue: h, sat }) => (
                  <div key={key} className="flex-1 flex flex-col items-center gap-1">
                    <button
                      onClick={() => handleSwatchClick(key)}
                      style={{
                        width: '100%', height: 30, borderRadius: 6,
                        background: key === 'bg'
                          ? `hsl(${h}, ${35 * sat / 100}%, 18%)`
                          : `hsl(${h}, ${65 * sat / 100}%, 42%)`,
                        border: activePicker === key ? '2px solid #f59e0b' : '1px solid rgba(255,255,255,0.15)',
                      }}
                    />
                    <span style={{ fontSize: 9, color: '#64748b', textAlign: 'center', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lbl}</span>
                  </div>
                ))}
              </div>
              {activePicker !== null && (
                <ColorPicker
                  cfg={cfg}
                  activeKey={activePicker}
                  onSwitch={setActivePicker}
                  onClose={closePicker}
                />
              )}
            </>
          );
        })()}

        {/* Border */}
        <div className="px-2 pb-3 flex items-center flex-wrap gap-1">
          <button
            className="text-xs px-3 py-1.5 rounded font-medium mr-1"
            style={colors.borderEnabled
              ? { background: '#f59e0b', color: '#000' }
              : { background: '#1e293b', color: '#64748b' }}
            onClick={() => setColors(c => ({ ...c, borderEnabled: !c.borderEnabled }))}
          >
            Border
          </button>
          {colors.borderEnabled && BORDER_PRESETS.map(color => (
            <Swatch key={color} color={color} selected={colors.borderColor === color} onClick={() => setters.setBorderColor(color)} />
          ))}
        </div>

        <div className="border-t border-slate-800 mx-2 mb-2" />

        <StatsPanel stats={pack.stats} values={statValues} accentHue={colors.accentHue} />
        <div className="border-t border-slate-800 mx-2 mb-2" />

        {/* Background effects */}
        <div className="px-3 pb-3">
          <div className="text-xs text-slate-500 px-1 pb-2">Background Effect</div>
          <div className="flex gap-1 mb-3">
            {(['none', 'stars', 'warp', 'nebula'] as EffectType[]).map(e => (
              <button
                key={e}
                className="flex-1 text-xs py-1.5 rounded font-medium"
                style={effectType === e
                  ? { background: '#78350f', border: '1px solid #f59e0b', color: '#fcd34d' }
                  : { background: '#1e293b', border: '1px solid #1e293b', color: '#64748b' }}
                onClick={() => setEffectType(e)}
              >
                {e === 'none' ? 'Off' : e[0].toUpperCase() + e.slice(1)}
              </button>
            ))}
          </div>
          {effectType !== 'none' && (
            <div className="flex items-center gap-3 px-1">
              <span className="text-xs text-slate-500 shrink-0">Slow</span>
              <input
                type="range"
                min={0}
                max={100}
                value={Math.round(effectSpeed * 100)}
                onChange={e => setEffectSpeed(parseInt(e.target.value) / 100)}
                className="flex-1"
                style={{ '--track-color': '#f59e0b' } as CSSProperties}
              />
              <span className="text-xs text-slate-500 shrink-0">Fast</span>
            </div>
          )}
        </div>

        <div className="border-t border-slate-800 mx-2 mb-2" />

        {/* Fleet with remove buttons */}
        <div className="px-2 pb-2">
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {fleet.map(entry => (
              <div key={entry.id} className="relative shrink-0">
                <button
                  className="flex flex-col items-center min-w-[52px] bg-slate-800 rounded px-1 pt-1 pb-2"
                  onClick={() => {
                    setBuild({ ...entry.build });
                    setColors({ ...entry.colors });
                  }}
                >
                  <div className="text-[9px] text-slate-300 truncate max-w-[48px]">{entry.build.name ?? '—'}</div>
                  <div className="text-[9px] text-slate-500 truncate max-w-[48px]">
                    {entry.build[baseCategoryId] ? pack.hulls[entry.build[baseCategoryId] as string]?.name : '—'}
                  </div>
                </button>
                <button
                  className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center leading-none"
                  style={{ background: '#334155', color: '#94a3b8', fontSize: 9 }}
                  onClick={() => removeFleet(entry.id)}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              className="shrink-0 text-[10px] text-slate-500 hover:text-slate-300 px-2 self-center"
              onClick={() => saveFleet({ build, colors })}
            >
              + Save
            </button>
          </div>
        </div>

        <div className="border-t border-slate-800 mx-2 mb-2" />

        {/* Export section */}
        <ExportPanel
          pack={pack}
          build={build}
          adapters={adapters}
          payloadToShare={{ build, colors }}
          exportPng={exportPng}
          exportAns={exportAns}
          exportSvg={exportSvg}
          getPngDataUrl={getPngDataUrl}
        />

        <div className="text-center pb-4 pt-2">
          <span style={{ fontSize: 10, color: '#475569' }}>{__APP_VERSION__}</span>
        </div>
      </div>

      {nameEditorOpen && (
        <NameEditor
          build={build}
          identity={adapters.identity}
          namingLabel={pack.metadata?.namingLabel}
          onSave={handleNameSave}
          onClose={() => setNameEditorOpen(false)}
        />
      )}
    </div>
  );
}
